import { Component, DestroyRef, effect, inject, Injector, input, OnInit, TemplateRef, viewChild } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  defer,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  tap
} from 'rxjs';
import { OrgAdminEndpointsService } from '../../../services/org-service/org-admin-endpoints.service';
import { AsyncPipe } from '@angular/common';
import { GenericTableComponent, TableColumn } from '../../core/generic-table/generic-table.component';
import { User } from '../org-model/user';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TableSettingsComponent } from '../../core/table-settings/table-settings.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TonalButtonComponent } from '../../core/tonal-button/tonal-button.component';
import { MatInput } from '@angular/material/input';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { SysNotificationResponse } from '../org-model/sys-notification-response.model';
import { SbSpinnerComponent } from '../../sb-client/sb-spinner/sb-spinner.component';
import { OrgUserListTestIds } from './org-user-list.test-ids';
import { LoadingService } from '../../../services/loading/loading.service';
import { OrgGeneralUserListComponent } from '../org-general-user-list/org-general-user-list.component';

@Component({
  selector: 'app-org-user-list',
  imports: [
    AsyncPipe,
    GenericTableComponent,
    MatSelectModule,
    MatFormFieldModule,
    TableSettingsComponent,
    FormsModule,
    TonalButtonComponent,
    MatInput,
    ButtonComponent,
    MatAutocomplete,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    CdkTextareaAutosize,
    SbSpinnerComponent
  ],
  templateUrl: './org-user-list.component.html',
  styleUrl: './org-user-list.component.scss'
})
/**
 * Die OrgUserListComponent zeigt eine Liste der Synpase-Nutzer.
 * BEGRÜNDUNG: Es handelt sich um eine Angular-Komponente, weshalb keine AFO erfüllt wird.
 */
export class OrgUserListComponent implements OnInit {
  userResponse$: Observable<User[]> = of([]);
  domain = input.required<string>();
  pageIndex = 0;
  itemsPerPage = 10;
  total = 0;
  dir = 'f';
  searchTerm = '';
  showSpinner = false;
  columns: TableColumn<User>[] = [];
  totalFiltered = 0;
  displayedColumns: TableColumn<User>[] = [];
  selectedOption = 'allUsers'; // Das ist die Default-Option
  options = [
    {
      value: 'allUsers',
      label: 'Alle Nutzer',
      dataTestId: OrgUserListTestIds.filterAllUsers
    },
    {
      value: 'deactivated',
      label: 'Ohne gelöschte Benutzer',
      dataTestId: OrgUserListTestIds.filterDeactivatedUsers
    }
  ];
  readonly statusTemplate = viewChild.required<TemplateRef<never>>('statusTemplate');

  userOptions: User[] | undefined;
  filteroption$ = new BehaviorSubject<User[]>([]);
  sysNotificationForm!: FormGroup;
  protected readonly testIds = OrgUserListTestIds;
  protected readonly matDialog = inject(MatDialog);
  protected readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);
  private readonly orgAdminEndpoints = inject(OrgAdminEndpointsService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);

  private readonly parent = inject(OrgGeneralUserListComponent);

  private readonly _searchEffect = effect(() => {
    this.searchTerm = this.parent.searchTerm();
    this.pageIndex = 0;
    this.loadUsers();
  });

  ngOnInit(): void {
    this.columns = [
      { key: 'displayname', label: 'Anzeigename', sortable: true },
      { key: 'name', label: 'TI-M-Adresse', sortable: false },
      { key: 'deactivated', label: 'Status', type: 'custom', template: this.statusTemplate() }
    ];
    this.displayedColumns = [...this.columns];
    this.matDialog.afterAllClosed.subscribe(() => {
      this.userOptions = [];
      this.searchTerm = '';
    });
    effect(
      () => {
        this.sysNotificationForm = this.fb.group({
          user_id: [],
          content: this.fb.group({
            body: ['', Validators.required],
            msgtype: ['m.text']
          })
        });
        this.loadUsers();
      },
      { injector: this.injector }
    );
  }

  loadUsers(): void {
    const queryParams: Record<string, string | number | boolean | undefined> = {
      limit: this.itemsPerPage,
      from: this.pageIndex * this.itemsPerPage,
      dir: this.dir,
      name: this.searchTerm,
      deactivated: false,
      guests: false,
      not_user_type: '',
      order_by: 'displayname'
    };

    switch (this.selectedOption) {
      case 'deactivated':
        queryParams['deactivated'] = false;
        break;
      case 'no-guests':
        queryParams['deactivated'] = true;
        queryParams['guests'] = false;
        break;
      case 'bots':
        queryParams['deactivated'] = true;
        queryParams['guests'] = true;
        queryParams['not_user_type'] = 'bot';
        break;
      case 'support':
        queryParams['deactivated'] = true;
        queryParams['guests'] = true;
        queryParams['not_user_type'] = 'support';
        break;
      case 'allUsers':
        queryParams['deactivated'] = true;
    }

    this.userResponse$ = defer(() => {
      this.loadingService.start();

      return this.orgAdminEndpoints.getUsers(this.domain(), queryParams).pipe(
        tap(resp => {
          this.total = resp.result.total;
          this.totalFiltered = resp.result.total;
        }),
        map(response => {
          return response.result?.users ?? [];
        }),
        catchError(() => {
          this.snackbarService.error('Es konnten keine User geladen werden');
          return of([] as User[]);
        }),
        finalize(() => {
          this.loadingService.stop();
        })
      );
    });
  }

  searchForSys(eventTarget: EventTarget): void {
    this.searchTerm = (eventTarget as HTMLInputElement).value;
    if (this.searchTerm.length >= 3 || this.searchTerm.length === 0) {
      this.loadNotifyUsers();
    }
  }

  loadNotifyUsers(): void {
    this.orgAdminEndpoints
      .getUsers(this.domain(), { name: this.searchTerm })
      .pipe(
        map(resp => {
          return resp.result?.users ?? [];
        }),
        tap(users => {
          this.userOptions = users;
          this.filteroption$.next(this._filterUser(this.sysNotificationForm.controls['user_id'].value ?? ''));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.loadUsers();
  }

  onSortChange(sort: { dir: string }): void {
    if (sort.dir === 'asc') {
      this.dir = 'b';
    } else {
      this.dir = 'f';
    }
    this.pageIndex = 0;
    this.loadUsers();
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.loadUsers();
  }

  navigateToUserSettings(event: { element: User }): void {
    this.router.navigate(['org-admin-client/v2', this.domain(), 'general-users', 'signed-in', event.element.name]);
  }

  sendSysNotification(): void {
    this.showSpinner = true;
    const userValues = this.sysNotificationForm.value.user_id;
    if (userValues) {
      const userId = userValues.split(' ')[0];
      this.sysNotificationForm.controls['user_id'].setValue(userId);
      this.postNotification().subscribe({
        error: () => {
          this.resetForm();
          this.showSpinner = false;
        },
        complete: () => {
          this.resetForm();
          this.showSpinner = false;
          this.matDialog.closeAll();
        }
      });
    } else {
      this.sendAllSysNotification();
    }
  }

  /**
   * Anzeige für das Frontend, splitted den Namen um im Frontend nur den disyplanamen anzuzeigen
   * @param value Die matrixId und der username
   */
  displayFn(value: string): string {
    const splittedValues = value.split(' ');
    return splittedValues[1];
  }

  protected sendAllSysNotification(): void {
    this.orgAdminEndpoints
      .getUsers(this.domain(), { deactivated: false, limit: this.total })
      .pipe(
        map(userList => userList.result.users),
        map(users => {
          if (!users) {
            this.snackbarService.error('Es kam zu einem Fehler beim Senden der Systemmeldung');
            throw new Error('No users found, when calling sendAllSysNotification');
          }
          return users;
        }),
        mergeMap(users => from(users)),
        concatMap(user => {
          this.sysNotificationForm.controls['user_id'].setValue(user.name);
          return this.postNotification();
        })
      )
      .subscribe({
        error: () => {
          this.resetForm();
          this.snackbarService.error('Es kam zu einem Fehler beim Senden der Systemmeldung');
          this.showSpinner = false;
        },
        complete: () => {
          this.resetForm();
          this.showSpinner = false;
          this.matDialog.closeAll();
        }
      });
  }

  protected resetForm(): void {
    this.sysNotificationForm.reset({
      user_id: null,
      content: {
        body: '',
        msgtype: 'm.text'
      }
    });
  }

  private postNotification(): Observable<SysNotificationResponse> {
    return this.orgAdminEndpoints.sendSysNotification(this.domain(), this.sysNotificationForm.value).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(err => {
        this.snackbarService.error('Es kam zu einem Fehler beim Senden der Systemmeldung');
        throw new Error('There was an error sendying sysNotifications', err);
      })
    );
  }

  /**
   * Filterung der User nach Namen
   * @param name der Name des Users
   * @private
   */
  private _filterUser(name: string): User[] {
    const userName = name.trim().toLowerCase();
    return this.userOptions?.filter(user => user.displayname!.toLowerCase().includes(userName)) ?? [];
  }
}
